import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import * as Sentry from '@sentry/react';
import { produce } from 'immer';
import debounce from 'just-debounce-it';
import { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	type Control,
	Controller,
	type FieldValues,
	type UseFormRegister,
	useForm,
} from 'react-hook-form';
import TipTapEditorV3 from '@/components/TipTapEditorV3/TipTapEditorV3';
import { EditorType } from '@/Enums/enum';
import useSaveDraft from '@/hooks/useSaveDraft';
import { useTabLabels } from '@/hooks/useTabLabels';
import { DragIcon, TrashCanIcon } from '@/icons/index';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CourseDiff from '../CourseDiff';
import CourseEditContext from '../CourseEditContext';

import CourseButtonGroup from './CourseButtonGroup';
import CourseSubs from './CourseSubs';
import { StyledUnit, StyledUnitDetails, StyledUnitGroup } from './CourseUnits.Styles';

// Enhanced type definitions
interface SkillBlob {
	focusId: string;
	courseFocusId: string;
	focusTitle: string;
	focusOrder: number;
}

interface UnitsOfFocusDraggableProps {
	unitIdx: number;
	removeSkill: (skill: { [key: string]: string }) => void;
	isApprovalMode: boolean;
	canEdit: boolean;
	courseFocusId: string;
	focusId: string;
	focusTitle: string;
	focusOrder: number;
	focusSkills: FocusSkillEntity[];
	control: Control<FieldValues>;
	updateUnitTitle: (courseFocusId: string, value: string) => void;
	saveAfterCommenting: (courseFocusId: string, value: string) => void;
	register: UseFormRegister<FieldValues>;
	inputFocusRef: { current: boolean };
	draftFocus: CourseFocusEntity;
	publishedFocus: CourseFocusEntity | undefined;
	removeSetFocus: (courseFocusId: string) => void;
	renderFocusChildren: (courseFocus: CourseFocusEntity | undefined) => string;
}

const UnitsOfFocusDraggable = memo(
	({
		removeSkill,
		unitIdx,
		isApprovalMode,
		canEdit,
		courseFocusId,
		focusId,
		focusTitle,
		focusOrder,
		focusSkills,
		control,
		updateUnitTitle,
		saveAfterCommenting,
		register,
		inputFocusRef,
		draftFocus,
		publishedFocus,
		removeSetFocus,
		renderFocusChildren,
	}: UnitsOfFocusDraggableProps) => {
		const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
			id: courseFocusId,
			disabled: isApprovalMode || !canEdit,
		});

		const style = useMemo(
			() => ({
				transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
				transition,
			}),
			[transform, transition]
		);

		const skillBlobData = useMemo(
			(): SkillBlob => ({
				focusId,
				courseFocusId,
				focusTitle: draftFocus?.focus?.focus_title || '',
				focusOrder: focusOrder ?? 0,
			}),
			[focusId, courseFocusId, focusOrder, draftFocus]
		);

		return (
			<StyledUnit ref={setNodeRef} style={style} data-testid={`unit-of-focus-${unitIdx}`}>
				{!isApprovalMode && canEdit && (
					<div className="drag-icon-wrapper" {...attributes} {...listeners}>
						<DragIcon />
					</div>
				)}
				<div className="single-unit">
					{!isApprovalMode && (
						<>
							<Controller
								control={control}
								name={`title-${focusId}`}
								defaultValue={focusTitle}
								render={({ field }) => (
									<TipTapEditorV3
										content={field.value}
										onChange={(e) => {
											updateUnitTitle(courseFocusId, e);
										}}
										placeholder="Add course unit of focus..."
										type={`${EditorType.UNITS}-${courseFocusId}`}
										isEditable={canEdit || !isApprovalMode}
										id={courseFocusId}
										commentAddedCallback={(value) => saveAfterCommenting(courseFocusId, value)}
									/>
								)}
							/>
							<input
								readOnly
								hidden
								defaultValue={JSON.stringify(skillBlobData)}
								{...register(`focusBlob-${focusId}`)}
							/>
						</>
					)}
					{canEdit && !isApprovalMode && (
						<TrashCanIcon passedEvent={() => removeSetFocus(courseFocusId)} />
					)}
				</div>
				<CourseButtonGroup courseFocusId={courseFocusId} inputFocusRef={inputFocusRef} />
				<div className="diff-container">
					<CourseDiff
						title="Title"
						original={renderFocusChildren(publishedFocus)}
						draft={renderFocusChildren(draftFocus)}
					/>
				</div>
				<StyledUnitDetails>
					{focusSkills?.map((skill: FocusSkillEntity) => {
						const { focuses_skills_id: focusSkillId } = skill;
						const { skill_text: skillText, skill_id: skillId, skill_type: skillType } = skill.skill;

						return (
							<CourseSubs
								key={`skill-${focusSkillId}-${skillId}`}
								courseFocusId={courseFocusId}
								focusSkillId={focusSkillId}
								skillId={skillId}
								skillText={skillText}
								skillType={skillType}
								isApprovalMode={isApprovalMode}
								canEdit={canEdit}
								removeSkill={removeSkill}
								register={register}
								inputFocusRef={inputFocusRef}
							/>
						);
					})}
				</StyledUnitDetails>
				<input readOnly hidden value={courseFocusId} {...register(`id-${focusId}`)} />
			</StyledUnit>
		);
	}
);

interface CourseUnitsFormProps {
	register: UseFormRegister<FieldValues>;
	inputFocusRef: { current: boolean };
}

const CourseUnitsForm = ({ register, inputFocusRef }: CourseUnitsFormProps) => {
	const {
		removeFocus,
		removeSkill,
		canEdit,
		courseData,
		courseDraftData,
		isApprovalMode,
		saveDraft,
		updateFocus,
		updateFocusOrder,
	} = useContext(CourseEditContext);

	const { control } = useForm();
	const coursesFocuses = useMemo(
		() => courseData.courses_focuses as CourseFocusEntity[],
		[courseData.courses_focuses]
	);
	const coursesDraftFocuses = useMemo(
		() => courseDraftData.courses_focuses,
		[courseDraftData.courses_focuses]
	);

	const { updateCommentTypes } = useCourseEditStore();

	// Memoized callback functions to prevent unnecessary re-renders
	const removeSetFocus = useCallback(
		(id: string) => {
			removeFocus(id);
			inputFocusRef.current = false;
		},
		[removeFocus, inputFocusRef]
	);

	const tabLabels = useTabLabels();

	const renderFocusChildren = useCallback(
		(focus: CourseFocusEntity | undefined) => {
			if (!focus?.focus) return '';

			const skills = focus.focus.focuses_skills ?? [];
			const skillLines = skills
				.map((item: FocusSkillEntity) => {
					const typeLabel = tabLabels[item.skill.skill_type] ?? tabLabels.skill;
					return `[${typeLabel}] ${item.skill.skill_text}`;
				})
				.join('</br>');

			return `Title: </br>${focus.focus.focus_title ?? ''}</br></br>${skillLines}`;
		},
		[tabLabels]
	);

	const updateUnitTitle = useCallback(
		(parentId: string, value: string) => {
			if (!coursesDraftFocuses) return;
			const updatedFocuses = produce(coursesDraftFocuses, (draft) => {
				const focus = draft.find((item) => item.courses_focuses_id === parentId);
				if (focus) {
					focus.focus.focus_title = value;
				}
			});
			updateFocus(updatedFocuses);
		},
		[coursesDraftFocuses, updateFocus]
	);

	const debouncedUpdateUnitTitle = useMemo(() => debounce(updateUnitTitle, 150), [updateUnitTitle]);

	const [isCommented, setIsCommented] = useState(false);
	const saveAfterCommenting = useCallback(
		(parentId: string, value: string) => {
			if (coursesDraftFocuses) {
				const updatedFocuses = produce(coursesDraftFocuses, (draft) => {
					const focus = draft.find((item) => item.courses_focuses_id === parentId);
					if (focus) {
						focus.focus.focus_title = value;
					}
				});
				updateFocus(updatedFocuses);
			}
			setIsCommented(!isCommented);
		},
		[coursesDraftFocuses, updateFocus, isCommented]
	);

	useSaveDraft(isCommented, saveDraft, setIsCommented);

	const sortedFocuses = useMemo(() => {
		// Create a copy before sorting since Immer freezes arrays
		return [...(coursesDraftFocuses ?? [])].sort(
			(a: CourseFocusEntity, b: CourseFocusEntity) => a.focus.focus_order - b.focus.focus_order
		);
	}, [coursesDraftFocuses]);

	const onDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;

			if (!over || active.id === over.id) {
				return;
			}

			const oldIndex = sortedFocuses.findIndex(
				(item: CourseFocusEntity) => item.courses_focuses_id === active.id
			);
			const newIndex = sortedFocuses.findIndex(
				(item: CourseFocusEntity) => item.courses_focuses_id === over.id
			);

			// Validate indices before proceeding
			if (oldIndex === -1 || newIndex === -1) {
				Sentry.captureMessage('Invalid drag indices in focus reordering', {
					level: 'warning',
					extra: { oldIndex, newIndex },
				});
				return;
			}

			const reorderedFocuses = arrayMove(sortedFocuses, oldIndex, newIndex);

			const updatedFocuses = reorderedFocuses.map((focus: CourseFocusEntity, index: number) => ({
				...focus,
				focus: {
					...focus.focus,
					focus_order: index,
				},
			}));

			updateFocusOrder(updatedFocuses);
		},
		[sortedFocuses, updateFocusOrder]
	);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	useEffect(() => {
		updateCommentTypes([EditorType.UNITS]);
	}, [updateCommentTypes]);

	// Extract IDs for SortableContext
	const focusIds = useMemo(() => {
		return sortedFocuses.map((focus: CourseFocusEntity) => focus.courses_focuses_id);
	}, [sortedFocuses]);

	// Early return for empty state
	if (isApprovalMode && (!coursesDraftFocuses || coursesDraftFocuses.length === 0)) {
		return (
			<StyledUnitGroup>
				<p className="mb-8 text-primary">No {tabLabels.units.toLowerCase()} present.</p>
			</StyledUnitGroup>
		);
	}

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
			<SortableContext items={focusIds} strategy={verticalListSortingStrategy}>
				<StyledUnitGroup>
					{sortedFocuses.map((unit: CourseFocusEntity, unitIdx: number) => {
						const { courses_focuses_id: courseFocusId } = unit;
						const {
							focus_title: focusTitle,
							focuses_skills: focusSkills,
							focus_order: focusOrder,
							focus_id: focusId,
						} = unit.focus;

						if (!(courseFocusId && focusId)) {
							Sentry.captureMessage('Invalid unit data: missing courseFocusId or focusId', {
								level: 'warning',
								extra: { courseFocusId, focusId },
							});
							return null;
						}

						// Find the published focus by ID (not by index) for accurate diff comparison
						const publishedFocus = coursesFocuses?.find(
							(f) => f.courses_focuses_id === courseFocusId
						);

						return (
							<UnitsOfFocusDraggable
								key={courseFocusId}
								unitIdx={unitIdx}
								isApprovalMode={isApprovalMode}
								canEdit={canEdit}
								courseFocusId={courseFocusId}
								focusId={focusId}
								removeSkill={removeSkill}
								focusTitle={focusTitle}
								focusOrder={focusOrder}
								focusSkills={focusSkills || []}
								control={control}
								updateUnitTitle={debouncedUpdateUnitTitle}
								saveAfterCommenting={saveAfterCommenting}
								register={register}
								inputFocusRef={inputFocusRef}
								draftFocus={unit}
								publishedFocus={publishedFocus}
								removeSetFocus={removeSetFocus}
								renderFocusChildren={renderFocusChildren}
							/>
						);
					})}
				</StyledUnitGroup>
			</SortableContext>
		</DndContext>
	);
};

export default CourseUnitsForm;
